import { createSignal, Show, type JSX } from 'solid-js';
import {
  addToPortfolio,
  removeFromPortfolioByStrainId,
} from '@lib/supabase/portfolio';
import { addWatch, removeWatch } from '@lib/supabase/watchlist';
import { authStore } from '@stores/session';

// Quick "+ Strains" / "+ Watch" buttons embedded in /strains and
// /stock list cards. The full toggles (PortfolioToggle, WatchToggle)
// still live on the strain detail page; this is a lighter affordance
// so a patient skimming the list can curate without clicking through.
//
// State model:
//   - Initial state comes from the parent page, which fetches
//     `fetchPortfolioStrainIds()` and `fetchWatchedStrainIds()` in
//     bulk so we don't hit one query per visible card.
//   - The button keeps a local optimistic signal that flips on
//     click. The parent's bulk Set is read-only from this
//     component's perspective; we rely on the optimistic flip for
//     immediate feedback and fall back to refetching the bulk Sets
//     when the user navigates away and back.
//   - On error we roll the optimistic state back so the UI matches
//     reality.
//
// Hidden entirely for anonymous users — the card still navigates to
// the strain detail where they'll be prompted to sign in. Showing
// disabled buttons here would just clutter dense list views.

type Kind = 'portfolio' | 'watchlist';

export default function CardQuickActions(props: {
  strainId: string;
  initialInPortfolio: boolean;
  initialInWatchlist: boolean;
}): JSX.Element {
  const [inPortfolio, setInPortfolio] = createSignal(props.initialInPortfolio);
  const [inWatchlist, setInWatchlist] = createSignal(props.initialInWatchlist);
  const [busy, setBusy] = createSignal<Kind | null>(null);
  const [error, setError] = createSignal<string | null>(null);

  async function togglePortfolio(e: MouseEvent) {
    // Cards on /strains wrap the strain link around the entire row;
    // stopPropagation prevents a click on the button from also
    // navigating to the detail page.
    e.preventDefault();
    e.stopPropagation();
    const wasIn = inPortfolio();
    setInPortfolio(!wasIn);
    setBusy('portfolio');
    setError(null);
    try {
      if (wasIn) await removeFromPortfolioByStrainId(props.strainId);
      else await addToPortfolio(props.strainId);
    } catch (err) {
      setInPortfolio(wasIn);
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function toggleWatchlist(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const wasIn = inWatchlist();
    setInWatchlist(!wasIn);
    setBusy('watchlist');
    setError(null);
    try {
      if (wasIn) await removeWatch(props.strainId);
      else await addWatch(props.strainId);
    } catch (err) {
      setInWatchlist(wasIn);
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Show when={authStore.user()}>
      <div class="flex items-center gap-1.5 mt-2">
        <button
          type="button"
          onClick={togglePortfolio}
          disabled={busy() === 'portfolio'}
          aria-pressed={inPortfolio()}
          aria-label={inPortfolio() ? 'Remove from My Strains' : 'Add to My Strains'}
          class={`text-[11px] rounded-full px-2.5 py-1 font-medium transition-colors disabled:opacity-60 ${
            inPortfolio()
              ? 'bg-brand-moss/20 text-brand-green border border-brand-moss'
              : 'bg-brand-hex text-brand-green hover:bg-brand-hex-deep'
          }`}
        >
          {inPortfolio() ? '✓ In strains' : '+ My Strains'}
        </button>
        <button
          type="button"
          onClick={toggleWatchlist}
          disabled={busy() === 'watchlist'}
          aria-pressed={inWatchlist()}
          aria-label={inWatchlist() ? 'Remove from Watchlist' : 'Add to Watchlist'}
          class={`text-[11px] rounded-full px-2.5 py-1 font-medium transition-colors disabled:opacity-60 ${
            inWatchlist()
              ? 'bg-brand-moss/20 text-brand-green border border-brand-moss'
              : 'bg-brand-hex text-brand-green hover:bg-brand-hex-deep'
          }`}
        >
          {inWatchlist() ? '✓ Watching' : '+ Watchlist'}
        </button>
        <Show when={error()}>
          <span class="text-[10px] text-red-700" role="alert">
            {error()}
          </span>
        </Show>
      </div>
    </Show>
  );
}
